import React, { useMemo, useState, useRef, FC } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import Description from 'src/presentation/components/pages/social/components/Description';
import EmptyState from 'src/presentation/components/pages/social/components/EmptyState';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import FBConfirmationModal from 'src/presentation/components/pages/social/components/FBConfirmationModal';
import FBSDKIframeLoader, {
  initiateFBLogin,
  useFBSDKIframeListener,
} from 'src/presentation/components/pages/social/components/FBSDKIframeLoader';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import InstagramConfigurationsSettingsModal from 'src/presentation/components/pages/social/InstagramConfigurationsSettingsModal';
import accountsResource, { Account, AccountType, createInstagramAccount } from 'src/actions/accounts/accountsResource';
import { FacebookPage } from 'src/models/Facebook/FacebookPage';
import agentsResource from 'src/actions/agent/agentsResource';
import messagingResource from 'src/actions/messaging/MessagingResource';
import systemConfigurationResource from 'src/actions/systemConfiguration/systemConfigurationResource';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import useResource, { allFinished, allSucceeded } from 'src/presentation/hooks/useResource';
import { RowActionContainer } from 'src/presentation/components/pages/shared/Table';
import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import { PrimaryButton, SecondaryButton } from '@ad-scripts/presentation/components/common/button';
import ExclamationCircleIcon from '@ad-scripts/presentation/components/lib/icons/exclamation_circle_icon';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import {
  accountsFilter,
  findAccountCreatorName,
  findUnassociatedFacebookPages,
} from 'src/presentation/components/pages/social/utils/utils';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import { toastError } from 'src/actions/toast';
import RowActions from 'src/presentation/components/pages/social/components/RowActions';
import { RootState } from 'src/actions/configuration/rootReducer';

const dataAidPrefix = 'instagram';
const emptyStateCopy = 'Need help setting up Instagram Messaging on Gladly? Check out our';
const emptyStateLinkText = 'Help Docs';
const docsLink = 'https://help.gladly.com/docs/set-up-and-configure-instagram-messaging';

const columnConfig = (messagingConfig, agents) => [
  {
    attr: 'alert',
    customRenderer: item => <Alert item={item} messagingConfig={messagingConfig} />,
    customStyle: () => ({ width: '10px' }),
  },
  {
    attr: 'facebookPageName',
    label: 'Page Name',
    customRenderer: item => item.content.name,
  },
  {
    attr: 'username',
    label: 'Username',
    customRenderer: item => item.content.instagramBusinessAccount.username,
  },
  {
    attr: 'facebookPageId',
    label: 'Page ID',
    customRenderer: item => item.content.id,
  },
  {
    attr: 'addedBy',
    label: 'Added By',
    customRenderer: item => findAccountCreatorName(item, agents),
  },
  {
    attr: 'addedOn',
    label: 'Added On',
    customRenderer: item => toDateTimeString(item.createdAt),
  },
  {
    attr: 'actions',
    label: '',
    customRenderer: item => (
      <RowActionContainer>
        <RowActions item={item} />
      </RowActionContainer>
    ),
  },
];

const InstagramConfigurations: TablePageRender = props => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    value: messagingConfig,
    loadingState: loadingStateMessage,
    errors: errorsMessage,
  } = useDocumentResource(messagingResource);
  const { loadingState: agentsLoadingState, values: agents = [] } = useResource(agentsResource);
  const {
    loadingState: accountsLoadingState,
    values: accounts = [],
    errors: accountErrors,
  } = useResource(accountsResource, {
    fetch: true,
  });
  const { loadingState: sysConfigLoadingState, value: sysConfig } = useDocumentResource(systemConfigurationResource, {
    fetch: true,
  });

  const filteredAccounts = accounts.filter(
    account => account.type === AccountType.InstagramDirect || account.type === AccountType.Instagram
  );
  const iframeRef = useRef(null);
  const [fbPages, setFbPages, fbError, setFbError] = useFBSDKIframeListener({
    targetOrigin: sysConfig.publicUrl,
  });

  const selectedFbPages = useMemo(
    () => findUnassociatedFacebookPages(accounts, fbPages, AccountType.Instagram),
    [accounts, fbPages]
  );

  const onClick = () => {
    initiateFBLogin({ iframeRef, targetOrigin: sysConfig.publicUrl });
  };

  if (fbError) {
    dispatch(toastError('Failed to connect with Facebook'));
    setFbError();
  }

  const onConfirmationCancel = () => {
    setFbPages();
  };

  const orgId = useSelector((state: RootState) => state.auth.orgId);

  const createOrUpdateInstagramAccount = ({ access_token, id, name, instagram_business_account }: FacebookPage) => {
    const createAccount = (toastMessage: string) => {
      dispatch(
        accountsResource.effects.add(
          {},
          createInstagramAccount(id, access_token, name, instagram_business_account, AccountType.Instagram),
          {
            disableWindowBack: true,
            customToastMsg: toastMessage,
          }
        )
      );
    };

    const updateAccount = (accountId: string, toastMessage: string) => {
      dispatch(
        accountsResource.effects.update(
          { orgId, accountId },
          { authToken: access_token },
          { customToastMsg: toastMessage }
        )
      );
    };

    // If comments not enabled, always create new account
    if (!messagingConfig.handleInstagramComments) {
      const toastMessage = `Successfully added Instagram Account ${name}`;
      createAccount(toastMessage);
      return;
    }

    // Check for existing INSTAGRAM_DIRECT account to update
    const existingDMAccount = accounts.find(
      account =>
        account.type === AccountType.InstagramDirect &&
        account.content.id === id &&
        account.content.instagramBusinessAccount?.id === instagram_business_account?.id
    );

    if (existingDMAccount) {
      updateAccount(existingDMAccount.id, `Successfully updated Instagram Account ${name}`);
    } else {
      createAccount(`Successfully added Instagram Account ${name}`);
    }
  };

  const onConfirmationOk = () => {
    selectedFbPages.forEach(createOrUpdateInstagramAccount);
    setFbPages();
  };

  const renderConfirmationModal = () => {
    if (fbPages) {
      if (selectedFbPages.length === 0) {
        return null;
      }
      return (
        <FBConfirmationModal
          description={`Please confirm the changes to Instagram`}
          selectedFBPages={selectedFbPages}
          onConfirmationCancel={onConfirmationCancel}
          onConfirmationOk={onConfirmationOk}
          title={'Manage Instagram Accounts'}
          warningContent={<WarningContent />}
        />
      );
    }
    return null;
  };

  if (fbError) {
    dispatch(toastError('Failed to connect to Instagram. Please try again soon'));
    setFbError();
  }

  const openSettingsModal = () => setIsModalOpen(true);
  const closeSettingsModal = () => setIsModalOpen(false);
  const shouldCreateNewInstagramAccount = accounts.some(
    (account: Account) => account.type === AccountType.InstagramDirect
  );

  const actions = filteredAccounts.length ? (
    <>
      <StyledSecondaryButton onClick={openSettingsModal}>Settings</StyledSecondaryButton>
      <PrimaryButton data-aid={`${dataAidPrefix}-addNew`} onClick={onClick}>
        Manage Instagram Accounts
      </PrimaryButton>
    </>
  ) : null;

  const description = `Connect with your Gladly customers through Instagram. To learn more, visit`;
  return (
    <>
      {renderConfirmationModal()}
      {allSucceeded(sysConfigLoadingState) ? (
        <FBSDKIframeLoader accountType={AccountType.Instagram} iframeRef={iframeRef} publicUrl={sysConfig.publicUrl} />
      ) : null}
      <TablePage
        actions={actions}
        columnConfig={columnConfig(messagingConfig, agents)}
        data-aid={`${dataAidPrefix}-page`}
        description={
          <Description description={description} helpDocsLink={docsLink} linkText={'Set Up and Configure Instagram'} />
        }
        emptyStateRender={
          <EmptyState
            onButtonClick={onClick}
            buttonText={'Add Instagram Account'}
            copy={emptyStateCopy}
            docsLink={docsLink}
            linkText={emptyStateLinkText}
          />
        }
        errors={accountErrors}
        filterFunc={accountsFilter}
        isLoading={!allFinished(accountsLoadingState, agentsLoadingState)}
        items={filteredAccounts}
        name={'Instagram Accounts'}
        renderRowActions={() => null}
        resetErrors={accountsResource.actions.resetErrors}
        root={props.root}
      />
      {isModalOpen && (
        <InstagramConfigurationsSettingsModal
          errors={errorsMessage}
          iframeRef={iframeRef}
          isInstagramDirectAccountUpdateRequired={shouldCreateNewInstagramAccount}
          loadingState={loadingStateMessage}
          onClose={closeSettingsModal}
          targetOrigin={sysConfig.publicUrl}
          value={messagingConfig}
        />
      )}
    </>
  );
};

interface AlertProps {
  item: Account;
  messagingConfig: { handleInstagramComments: boolean };
}

/**
 * Show alert for INSTAGRAM_DIRECT accounts when Instagram comments are enabled
 * and handleInstagramComments is true
 */
function Alert({ item, messagingConfig }: AlertProps) {
  const shouldShowAlert = item.type === AccountType.InstagramDirect && messagingConfig.handleInstagramComments;

  if (!shouldShowAlert) {
    return null;
  }

  return (
    <Tooltip
      data-aid={`${dataAidPrefix}-alert-column-tooltip`}
      message={
        <TooltipMessage data-aid={`${dataAidPrefix}-column-tooltip-text`}>
          Comments are not turned on.
          <br />
          Try enabling comments again.
        </TooltipMessage>
      }
    >
      <StyledExclamationCircleIcon />
    </Tooltip>
  );
}

export default InstagramConfigurations;

type WarningContentProps = {};

const WarningContent: FC<WarningContentProps> = () => (
  <StyledAlertWarning>
    <b>Note:</b>&nbsp;Make sure you allow Gladly access to all messages through your Instagram mobile app. Visit&nbsp;
    <StyledExternalLink
      className="helpLink"
      data-aid={`${dataAidPrefix}-help-link`}
      label={'Set Up and Configure Instagram'}
      to={docsLink}
    />
    &nbsp;for more details.
  </StyledAlertWarning>
);

const StyledAlertWarning = styled(AlertWarning)`
  width: 320px;
  text-align: left;
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  margin-right: 16px;
`;

const StyledExternalLink = styled(ExternalLink)`
  text-decoration: underline;
  color: ${p => p.theme.colors.black};

  &:hover {
    text-decoration: underline;
  }
`;

const StyledExclamationCircleIcon = styled(ExclamationCircleIcon)`
  fill: ${p => p.theme.colors.red400};
  width: 16px;
`;

const TooltipMessage = styled.p`
  text-align: center;
  margin: 0;
  line-height: 162%;
`;
